import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { PiCalculator, PiPlus } from 'react-icons/pi';
import BottomSheet from '../BottomSheet';
import CalculatorModal from '../CalculatorModal';
import CategoryFormSheet from '../CategoryFormSheet';
import { useCategories } from '../../hooks/useCategories';
import { DateField, MoneyField, SelectField, TextField } from '../FormFields';
import HorizontalChipScroller from '../HorizontalChipScroller';
import MemberAttributionSelect from '../MemberAttributionSelect';
import '../EditItemModal/style.css';
import {
  TRANSACTION_STATUS,
  TRANSACTION_TYPES,
  WRITE_PAYLOAD_ERRORS,
  buildTransactionWritePayload,
  getDefaultStatusForType,
  getTodayCivilDate,
  isValidStatusForType,
  isValidTransactionType,
} from '../../domain/finance';
import {
  NO_RECURRENCE,
  RECURRENCE_END_NEVER,
  RECURRENCE_END_ON_DATE,
  RECURRENCE_OPTIONS,
  buildRecurrenceFormInput,
  getRecurrencePreview,
} from './recurrenceOptions';
import { getMemberLabel } from '../../domain/finance/memberAttribution';
import { getRemoteSettlement } from '../../services/connectivityService';

const getPeriodISO = (selectedPeriod) => {
  const match = String(selectedPeriod || '').match(
    /^(\d{1,2})\/(\d{4})$/
  );

  if (!match) return null;

  const [, rawMonth, year] = match;
  const month = rawMonth.padStart(2, '0');

  return `${year}-${month}`;
};

const getInitialTransactionDate = (selectedPeriod) => {
  const today = getTodayCivilDate();
  const selectedPeriodISO = getPeriodISO(selectedPeriod);

  if (!selectedPeriodISO) {
    return today;
  }

  const lastUsedDate =
    localStorage.getItem('lastUsedTransactionDate');

  /*
   * Reaproveita a última data somente se ela
   * pertencer ao período atualmente selecionado.
   */
  if (
    lastUsedDate &&
    lastUsedDate.startsWith(`${selectedPeriodISO}-`)
  ) {
    return lastUsedDate;
  }

  const currentPeriodISO = today.slice(0, 7);

  /*
   * Mês atual sem histórico:
   * usa hoje.
   */
  if (selectedPeriodISO === currentPeriodISO) {
    return today;
  }

  /*
   * Outro mês sem histórico:
   * começa no primeiro dia.
   */
  return `${selectedPeriodISO}-01`;
};
const AddItemModal = ({
  show,
  onClose,
  onSave,
  onSaveRecurring,
  initialType,
  initialDraft,
  selectedPeriod,
  accountId,
  currentRole,
  members = [],
  defaultAttributedToUid = '',
  defaultAttributedToName = 'Você',
}) => {
  const [nome, setNome] = useState('');
  const [valor, setValor] = useState('');
  const [data, setData] = useState(() =>
    getInitialTransactionDate(selectedPeriod)
  );
  const [categoria, setCategoria] = useState('');
  const [tipo, setTipo] = useState(() => {
    const candidate = initialType || localStorage.getItem('lastUsedType');
    return isValidTransactionType(candidate) ? candidate : TRANSACTION_TYPES.INCOME;
  });
  const [status, setStatus] = useState(() => {
    const lastUsedStatus = localStorage.getItem('lastUsedStatus');
    return isValidStatusForType(tipo, lastUsedStatus)
      ? lastUsedStatus
      : getDefaultStatusForType(tipo);
  });
  const [showCalculator, setShowCalculator] = useState(false);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const { expenseCategories, incomeCategories, createCategory } = useCategories(accountId);
  const selectableCategories = tipo === TRANSACTION_TYPES.INCOME ? incomeCategories : expenseCategories;
  const canManageCategories = ['owner', 'admin', 'editor'].includes(currentRole);
  const [fieldErrors, setFieldErrors] = useState({});
  const [repeat, setRepeat] = useState(NO_RECURRENCE);
  const [recurrenceEnd, setRecurrenceEnd] = useState(RECURRENCE_END_NEVER);
  const [untilDate, setUntilDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmationUnknown, setConfirmationUnknown] = useState(false);
  const logicalAttemptId = useRef(null);
  const previousShow = useRef(false);
  const mounted = useRef(true);
  const activeScope = useRef({ accountId, show });
  activeScope.current = { accountId, show };
  const [attributedToUid, setAttributedToUid] = useState(defaultAttributedToUid || '');
  const attributionOptions = [...members];
  if (defaultAttributedToUid && !attributionOptions.some((member) => (member.uid || member.id) === defaultAttributedToUid)) {
    attributionOptions.unshift({ uid: defaultAttributedToUid, displayName: defaultAttributedToName });
  }
  const selectedMember = attributionOptions.find((member) => (member.uid || member.id) === attributedToUid);

  const recurrencePreview = getRecurrencePreview({
    repeat,
    anchorDate: data,
    endMode: recurrenceEnd,
    untilDate,
  });

  const clearFieldError = (field) => {
    setFieldErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleTypeChange = (newType) => {
    setTipo(newType);
    clearFieldError('tipo');
    localStorage.setItem('lastUsedType', newType);
    const newStatus = getDefaultStatusForType(newType);
    setStatus(newStatus);
    localStorage.setItem('lastUsedStatus', newStatus);
  };

  const handleStatusChange = (newStatus) => {
    setStatus(newStatus);
    localStorage.setItem('lastUsedStatus', newStatus);
    clearFieldError('status');
  };

  const handleSave = async (event) => {
    event?.preventDefault();
    if (saving) return;

    const result = buildTransactionWritePayload({
      nome,
      amountInput: valor,
      date: data,
      categoria,
      tipo,
      status,
    });
    const recurrenceResult = buildRecurrenceFormInput({
      repeat,
      anchorDate: data,
      endMode: recurrenceEnd,
      untilDate,
    });
    const isRecurring = recurrenceResult.recurrence !== null;
    const nextErrors = {};

    if (result.errors.includes(WRITE_PAYLOAD_ERRORS.INVALID_AMOUNT)) {
      nextErrors.valor = 'Informe um valor válido, com no máximo duas casas decimais.';
    }
    if (result.errors.includes(WRITE_PAYLOAD_ERRORS.INVALID_DATE)) {
      nextErrors.data = 'Informe uma data válida.';
    }
    if (result.errors.includes(WRITE_PAYLOAD_ERRORS.INVALID_TYPE)) {
      nextErrors.tipo = 'Selecione um tipo válido.';
    }
    if (result.errors.includes(WRITE_PAYLOAD_ERRORS.INVALID_STATUS)) {
      nextErrors.status = 'Selecione um status compatível com o tipo.';
    }
    if (repeat !== NO_RECURRENCE && !nome.trim()) {
      nextErrors.nome = 'Informe uma descrição para a recorrência.';
    }
    if (recurrenceResult.errors.repeat) nextErrors.repeat = recurrenceResult.errors.repeat;
    if (recurrenceResult.errors.endMode) nextErrors.recurrenceEnd = recurrenceResult.errors.endMode;
    if (recurrenceResult.errors.untilDate) nextErrors.untilDate = recurrenceResult.errors.untilDate;
    if (recurrenceResult.errors.anchorDate) nextErrors.data = recurrenceResult.errors.anchorDate;

    setFieldErrors(nextErrors);
    if (!result.ok || !recurrenceResult.ok || (repeat !== NO_RECURRENCE && !nome.trim())) return;

    setSaving(true);
    setConfirmationUnknown(false);
    const accountIdAtStart = accountId;
    if (!logicalAttemptId.current) {
      logicalAttemptId.current = window.crypto?.randomUUID?.()
        || `tx_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
    }
    const clientOperationId = logicalAttemptId.current;
    const isCurrentScope = () => mounted.current
      && activeScope.current.show
      && activeScope.current.accountId === accountIdAtStart;
    try {
      if (isRecurring) {
        if (!onSaveRecurring) throw new Error('RECURRING_SAVE_HANDLER_REQUIRED');
        await onSaveRecurring({
          transaction: { ...result.payload, ...(selectedMember ? { attributedToUid, attributedToNameSnapshot: getMemberLabel(selectedMember) } : {}) },
          recurrence: recurrenceResult.recurrence,
        });
      } else {
        await onSave({ ...result.payload, clientOperationId, ...(selectedMember ? { attributedToUid, attributedToNameSnapshot: getMemberLabel(selectedMember) } : {}) });
      }

      if (!isCurrentScope()) return;
      localStorage.setItem('lastUsedTransactionDate', data);
      onClose();
    } catch (error) {
      const lateSettlement = getRemoteSettlement(error);
      if (lateSettlement) {
        if (isCurrentScope()) setConfirmationUnknown(true);
        try {
          await lateSettlement;
          if (!isCurrentScope()) return;
          setConfirmationUnknown(false);
          localStorage.setItem('lastUsedTransactionDate', data);
          onClose();
        } catch (lateError) {
          if (!isCurrentScope()) return;
          setConfirmationUnknown(false);
          console.error('A gravação pendente foi rejeitada:', lateError);
          setSaving(false);
        }
        return;
      }
      if (!isCurrentScope()) return;
      console.error('Erro ao salvar movimentação:', error);
      setSaving(false);
    }
  };
  useEffect(() => {
    if (isValidTransactionType(initialType)) {
      setTipo(initialType);
      setStatus((currentStatus) =>
        isValidStatusForType(initialType, currentStatus)
          ? currentStatus
          : getDefaultStatusForType(initialType)
      );
    }
  }, [initialType]);


  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  useEffect(() => {
    if (show && !previousShow.current) {
      logicalAttemptId.current = null;
      setSaving(false);
      setConfirmationUnknown(false);
    }
    previousShow.current = show;
  }, [show]);
  useEffect(() => {
    if (!show) return;
    setAttributedToUid(defaultAttributedToUid || '');

    setData(
      getInitialTransactionDate(selectedPeriod)
    );

    setFieldErrors((current) => ({
      ...current,
      data: undefined,
    }));
  }, [defaultAttributedToUid, show, selectedPeriod]);

  useEffect(() => {
    if (!show || !initialDraft) return;
    setNome(initialDraft.nome || '');
    setValor(Number.isSafeInteger(initialDraft.amountCents) ? (initialDraft.amountCents / 100).toFixed(2).replace('.', ',') : '');
    setData(initialDraft.date || getInitialTransactionDate(selectedPeriod));
    setCategoria(initialDraft.categoria || '');
    if (isValidTransactionType(initialDraft.tipo)) setTipo(initialDraft.tipo);
    if (isValidStatusForType(initialDraft.tipo, initialDraft.status)) setStatus(initialDraft.status);
    setFieldErrors({});
  }, [initialDraft, selectedPeriod, show]);


  useEffect(() => {
    if (!show) return;

    const initialDate =
      getInitialTransactionDate(selectedPeriod);

    setData(initialDate);

    setFieldErrors((current) => ({
      ...current,
      data: undefined,
    }));
  }, [show, selectedPeriod]);


  if (!show) return null;







  return (
    <>
      <BottomSheet
        title="Nova movimentação"
        onClose={onClose}
        size="lg"
        headerActions={(
          <button type="button" className="nf-sheet__action" onClick={() => setShowCalculator(true)} aria-label="Abrir calculadora">
            <PiCalculator size={20} aria-hidden="true" />
          </button>
        )}
        footer={(
          <>
            <button type="submit" form="new-transaction-form" className="nf-sheet__primary" disabled={saving}>
              {confirmationUnknown ? 'Confirmação pendente…' : saving ? 'Salvando…' : 'Salvar'}
            </button>
            <button type="button" className="nf-sheet__secondary" onClick={onClose} disabled={saving}>Cancelar</button>
          </>
        )}
      >
        <form id="new-transaction-form" className="transaction-sheet-form transaction-composer" onSubmit={handleSave}>
          <div className="transaction-composer__amount">
            <MoneyField
              id="add-transaction-amount"
              label="Valor"
              value={valor}
              onChange={(value) => { setValor(value); clearFieldError('valor'); }}
              error={fieldErrors.valor}
              hint="Você pode calcular o valor sem sair desta tela."
              required
            />
          </div>
          <SelectField id="add-transaction-type" label="Tipo da movimentação" value={tipo} onChange={handleTypeChange} error={fieldErrors.tipo} required>
            <option value={TRANSACTION_TYPES.INCOME}>{TRANSACTION_TYPES.INCOME}</option>
            <option value={TRANSACTION_TYPES.EXPENSE}>{TRANSACTION_TYPES.EXPENSE}</option>
          </SelectField>
          <div className="transaction-composer__categories">
            <span className="transaction-composer__label">Categorias rápidas</span>
            <HorizontalChipScroller className="transaction-composer__chips" ariaLabel="Categorias rápidas">
              {selectableCategories.slice(0, 5).map((category) => (
                <button
                  type="button"
                  key={category.id}
                  className={categoria === category.name ? 'is-selected' : ''}
                  aria-pressed={categoria === category.name}
                  onClick={() => setCategoria(category.name)}
                >
                  {category.name}
                </button>
              ))}
            </HorizontalChipScroller>
          </div>
          <TextField
            id="add-transaction-name"
            label="Descrição"
            value={nome}
            onChange={(value) => { setNome(value); clearFieldError('nome'); }}
            error={fieldErrors.nome}
            placeholder="Ex.: Almoço, salário, mercado…"
          />
          <SelectField id="add-transaction-category" label="Todas as categorias" value={categoria} onChange={setCategoria}>
            <option value="">Selecionar categoria</option>
            {selectableCategories.map((category) => <option value={category.name} key={category.id}>{category.name}</option>)}
          </SelectField>
          {canManageCategories && (
            <button type="button" className="transaction-composer__new-category" onClick={() => setShowCategoryForm(true)}>
              <PiPlus aria-hidden="true" /> Nova categoria
            </button>
          )}
          <MemberAttributionSelect id="add-transaction-attribution" label="Atribuir a" members={attributionOptions} value={attributedToUid} onChange={setAttributedToUid} />
          <DateField
            id="add-transaction-date"
            label="Data"
            value={data}
            onChange={(value) => { setData(value); clearFieldError('data'); }}
            error={fieldErrors.data}
            required
          />
          <SelectField
            id="add-transaction-repeat"
            label="Repetir"
            value={repeat}
            onChange={(value) => {
              setRepeat(value);
              clearFieldError('repeat');
              if (value === NO_RECURRENCE) {
                setRecurrenceEnd(RECURRENCE_END_NEVER);
                setUntilDate('');
                clearFieldError('untilDate');
              }
            }}
            error={fieldErrors.repeat}
          >
            {RECURRENCE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </SelectField>
          {repeat !== NO_RECURRENCE && (
            <div className="transaction-composer__recurrence">
              <SelectField
                id="add-transaction-recurrence-end"
                label="Termina"
                value={recurrenceEnd}
                onChange={(value) => {
                  setRecurrenceEnd(value);
                  clearFieldError('recurrenceEnd');
                  if (value === RECURRENCE_END_NEVER) {
                    setUntilDate('');
                    clearFieldError('untilDate');
                  }
                }}
                error={fieldErrors.recurrenceEnd}
              >
                <option value={RECURRENCE_END_NEVER}>Nunca</option>
                <option value={RECURRENCE_END_ON_DATE}>Em uma data</option>
              </SelectField>
              {recurrenceEnd === RECURRENCE_END_ON_DATE && (
                <DateField
                  id="add-transaction-until-date"
                  label="Data final"
                  value={untilDate}
                  onChange={(value) => { setUntilDate(value); clearFieldError('untilDate'); }}
                  error={fieldErrors.untilDate}
                  required
                />
              )}
              {recurrencePreview && (
                <p className="transaction-composer__recurrence-preview">{recurrencePreview}</p>
              )}
            </div>
          )}
          <SelectField id="add-transaction-status" label="Status" value={status} onChange={handleStatusChange} error={fieldErrors.status} required>
            <option value={tipo === TRANSACTION_TYPES.INCOME ? TRANSACTION_STATUS.RECEIVED : TRANSACTION_STATUS.PAID}>
              {tipo === TRANSACTION_TYPES.INCOME ? TRANSACTION_STATUS.RECEIVED : TRANSACTION_STATUS.PAID}
            </option>
            <option value={TRANSACTION_STATUS.PENDING}>{TRANSACTION_STATUS.PENDING}</option>
          </SelectField>
        </form>
      </BottomSheet>
      {showCategoryForm && (
        <CategoryFormSheet
          onClose={() => setShowCategoryForm(false)}
          onSave={async (input) => { const created = await createCategory(input); setCategoria(created.name); }}
        />
      )}
      {showCalculator && (
        <CalculatorModal

          onClose={() => setShowCalculator(false)}
          onUseValue={(value) => {
            setValor(value);
            clearFieldError('valor');
          }}
        />
      )}
    </>
  );
};

AddItemModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onSaveRecurring: PropTypes.func,
  initialType: PropTypes.string,
  initialDraft: PropTypes.shape({
    nome: PropTypes.string,
    amountCents: PropTypes.number,
    date: PropTypes.string,
    categoria: PropTypes.string,
    tipo: PropTypes.string,
    status: PropTypes.string,
  }),
  selectedPeriod: PropTypes.string,
  accountId: PropTypes.string,
  currentRole: PropTypes.string,
  members: PropTypes.array,
  defaultAttributedToUid: PropTypes.string,
  defaultAttributedToName: PropTypes.string,
};

export default AddItemModal;
